import { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Login } from "./src/pages/Login";
import { SignUp } from "./src/pages/SignUp";
import { AccountSetup } from "./src/components/AccountSetup";
import { Navigation } from "./src/components/Navigation";
import { Dashboard } from './src/pages/Dashboard';
import { Applications } from "./src/pages/Applications";
import { Projects } from "./src/pages/Projects";
import { ApplicationForm } from "./src/components/ApplicationForm";
import { Scans } from "./src/pages/Scans";
import { Team } from "./src/pages/Team";
import { Profile } from "./src/pages/Profile";
import { Settings } from "./src/pages/Settings";
import { Help } from "./src/pages/Help";
import { DownloadScanner } from "./src/pages/DownloadScanner";
import { Notifications } from "./src/pages/Notifications";
import { TermsOfService } from "./src/pages/TermsOfService";
import { PrivacyPolicy } from "./src/pages/PrivacyPolicy";
import { GDPRPolicy } from "./src/pages/GDPRPolicy";
import CookiePolicy from "./src/pages/CookiePolicy";
import ThirdPartyLicenseObligation from "./src/pages/ThirdPartyLicenseObligations";
import { NewScan } from "./src/components/NewScan";
import { ScanViewReport } from "./src/pages/ScanViewReport";
import { ProjectViewReport } from "./src/pages/ProjectViewReport";
import { ApplicationViewReport } from "./src/pages/ApplicationViewReport";
import { ResetPassword } from "./src/components/ResetPassword";
import ComponentsHub from "./src/pages/ComponentsHub";
import { GitHubCallback } from "./src/pages/GitHubCallback";
import { VerifyEmail } from "./src/pages/VerifyEmail";
import { Landing } from './src/pages/Landing';
import { PublicPricing } from './src/pages/PublicPricing';
import { Footer } from "./src/components/Footer";
import { useAuth } from './src/contexts/AuthContext';
import { Toaster } from 'sonner';
import { ProjectForm } from '@/components/ProjectForm';
import { Sidebar } from '@/components/Sidebar';
import { RequirePermission } from '@/components/RequirePermission';

function AppLayout() {
  const { user, logout, activeWorkgroupId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isGridLayout, setIsGridLayout] = useState(true);
  const [showNewScan, setShowNewScan] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewApplication, setShowNewApplication] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [routeParams, setRouteParams] = useState<any>({});
const [searchParams] = useSearchParams();
  useEffect(() => {
    const checkScreenSize = () => {
      const isSmallScreen = window.innerWidth <= 1280;
      setIsSidebarCollapsed(isSmallScreen);
      if (window.innerWidth <= 768) {
        setIsGridLayout(true);
      }
    };

    checkScreenSize();

    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const applicationsRefreshRef = useRef<(() => void) | null>(null);
  const projectsRefreshRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const params = { ...location.state };

    const projectsMatch = location.pathname.match(/^\/projects\/(\d+)$/);
    if (projectsMatch) {
      params.applicationId = parseInt(projectsMatch[1]);
    }

    const appReportMatch = location.pathname.match(/^\/application-view-report\/(\d+)$/);
    if (appReportMatch) {
      params.applicationId = parseInt(appReportMatch[1]);
    }

    const scansMatch = location.pathname.match(/^\/scans\/(\d+)$/);
    if (scansMatch) {
      params.projectId = parseInt(scansMatch[1]);
    }

    const componentReportMatch = location.pathname.match(/^\/component-view-report\/(\d+)$/);
    if (componentReportMatch) {
      params.scanId = parseInt(componentReportMatch[1]);
    }

    const projectReportMatch = location.pathname.match(/^\/project-view-report\/(\d+)$/);
    if (projectReportMatch) {
      params.projectId = parseInt(projectReportMatch[1]);
    }

    setRouteParams(params);
  }, [location.state, location.pathname]);

  const getCurrentRoute = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';

    const projectsMatch = path.match(/^\/projects\/(\d+)$/);
    if (projectsMatch) {
      return 'projects';
    }

    const appReportMatch = path.match(/^\/application-view-report\/(\d+)$/);
    if (appReportMatch) {
      return 'application-view-report';
    }

    const scansMatch = path.match(/^\/scans\/(\d+)$/);
    if (scansMatch) {
      return 'scans';
    }

    const componentReportMatch = path.match(/^\/component-view-report\/(\d+)$/);
    if (componentReportMatch) {
      return 'component-view-report';
    }

    const projectReportMatch = path.match(/^\/project-view-report\/(\d+)$/);
    if (projectReportMatch) {
      return 'project-view-report';
    }

    return path.substring(1);
  };

  const currentRoute = getCurrentRoute();

  const handleLayoutToggle = () => {
    setIsGridLayout(!isGridLayout);
  };

  const handleNewScan = () => {
    setShowNewScan(true);
  };

  const handleCloseNewScan = () => {
    setShowNewScan(false);
  };

  const handleNewProject = () => {
    setShowNewProject(true);
  };

  const handleCloseNewProject = () => {
    setShowNewProject(false);
  };

  const handleNewApplication = () => {
    setShowNewApplication(true);
  };

  const handleCloseNewApplication = () => {
    setShowNewApplication(false);
  };

  const handleApplicationSelect = (applicationId: number) => {
    setSelectedApplication(applicationId);
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleNavigate = (route: string, params?: any) => {
    if (route === 'new-project') {
      handleNewProject();
      return;
    }
    if (route === 'new-application') {
      handleNewApplication();
      return;
    }
    if (route === 'billing') {
      // Billing lives as a tab on the Team page now, not its own route.
      navigate('/team');
      return;
    }

    if (params && params.applicationId && route === 'projects') {
      navigate(`/projects/${params.applicationId}`, { state: params });
    } else if (params && params.projectId && route === 'scans') {
      navigate(`/scans/${params.projectId}`, { state: params });
    } else if (params && params.applicationId && route === 'application-view-report') {
      navigate(`/application-view-report/${params.applicationId}`, { state: params });
    } else if (params && params.projectId && route === 'project-view-report') {
      navigate(`/project-view-report/${params.projectId}`, { state: params });
    } else if (params && params.scanId && route === 'component-view-report') {
      navigate(`/component-view-report/${params.scanId}`, { state: params });
    } else {
      navigate(`/${route}`, { state: params });
    }
  };

  return (
    <div className="h-screen bg-background flex flex-col max-w-full overflow-x-hidden">
      <div className="flex flex-1 min-h-0 max-w-full">
        <Sidebar
          currentRoute={currentRoute}
          onRouteChange={(route) => navigate(`/${route}`)}
          isCollapsed={isSidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <div className="flex-1 flex flex-col min-h-0 min-w-0 max-w-full">
          <Navigation
            currentRoute={currentRoute}
            isGridLayout={isGridLayout}
            onLayoutToggle={handleLayoutToggle}
            onNewScan={handleNewScan}
            onNavigate={handleNavigate}
            onLogout={logout}
            onApplicationSelect={handleApplicationSelect}
            profileImageUrl={user?.avatar || ''}
            routeParams={routeParams}
            searchParams={searchParams}
          />

          <main className="flex-1 overflow-auto min-w-0 max-w-full">
            <Routes>
              {/* <Route path="/" element={<Dashboard onNavigate={handleNavigate} workgroupId={activeWorkgroupId!} />} /> */}
              <Route path="/dashboard" element={<Dashboard onNavigate={handleNavigate} workgroupId={activeWorkgroupId!} />} />
              <Route path="/applications" element={
                <Applications
                  isGridLayout={isGridLayout}
                  onNavigate={handleNavigate}
                  workgroupId={activeWorkgroupId!}
                  onRefreshApplications={(refreshFn) => {
                    applicationsRefreshRef.current = refreshFn;
                  }}
                />
              } />
              <Route path="/projects" element={
                <Projects
                  isGridLayout={isGridLayout}
                  onNavigate={handleNavigate}
                  workgroupId={activeWorkgroupId!}
                  onRefreshProjects={(refreshFn) => {
                    projectsRefreshRef.current = refreshFn;
                  }}
                />
              } />

              <Route path="/projects/:applicationId" element={
                <Projects
                  isGridLayout={isGridLayout}
                  onNavigate={handleNavigate}
                  workgroupId={activeWorkgroupId!}
                  applicationId={routeParams?.applicationId}
                  onRefreshProjects={(refreshFn) => {
                    projectsRefreshRef.current = refreshFn;
                  }}
                />
              } />

              <Route path="/scans" element={
                <Scans
                  isGridLayout={isGridLayout}
                  onNavigate={handleNavigate}
                  workgroupId={activeWorkgroupId!}
                />
              } />

              <Route path="/scans/:projectId" element={
                <Scans
                  isGridLayout={isGridLayout}
                  onNavigate={handleNavigate}
                  workgroupId={activeWorkgroupId!}
                  projectId={routeParams?.projectId}
                />
              } />
              <Route path="/components-hub" element={
                <RequirePermission module="components" action="read">
                  <ComponentsHub workgroupId={activeWorkgroupId!} />
                </RequirePermission>
              } />
              <Route path="/team" element={
                <RequirePermission module="teams" action="read">
                  <Team workgroupId={activeWorkgroupId!} />
                </RequirePermission>
              } />
              <Route path="/notifications" element={<Notifications onNavigate={handleNavigate} />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              {/* Billing now lives as a tab on the Team page, since it's per-workgroup. */}
              <Route path="/billing" element={<Navigate to="/team" replace />} />
              <Route path="/help" element={<Help onNavigate={handleNavigate} />} />
              <Route path="/third-party-list" element={<ThirdPartyLicenseObligation />} />
              <Route path="/download-scanner" element={<DownloadScanner />} />

              <Route path="/component-view-report/:scanId" element={<ScanViewReport onNavigate={handleNavigate} scanId={routeParams?.scanId} />} />

              <Route path="/project-view-report/:projectId" element={<ProjectViewReport onNavigate={handleNavigate} projectId={routeParams?.projectId} />} />

              <Route path="/application-view-report/:applicationId" element={<ApplicationViewReport onNavigate={handleNavigate} applicationId={routeParams?.applicationId} />} />

              {/* Catch any unmatched path (e.g. a stale notification/bookmark URL) instead of rendering a blank page. */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
            <Footer />
        </div>
      </div>

    

      {showNewScan && (
        <NewScan
          isOpen={showNewScan}
          onClose={handleCloseNewScan}
          onNavigate={handleNavigate}
          workgroupId={activeWorkgroupId!}
          applicationId={routeParams.applicationId}
          projectId={routeParams.projectId}
        />
      )}

      {showNewProject && (
        <ProjectForm
          isOpen={showNewProject}
          onClose={handleCloseNewProject}
          applicationId={routeParams.applicationId}
          workgroupId={activeWorkgroupId!}
          onSuccess={() => {
            if (projectsRefreshRef.current) {
              projectsRefreshRef.current();
            }
          }}
        />
      )}

      {showNewApplication && (
        <ApplicationForm
          isOpen={showNewApplication}
          workgroupId={activeWorkgroupId!}
          onClose={handleCloseNewApplication}
          onSuccess={() => {
            if (applicationsRefreshRef.current) {
              applicationsRefreshRef.current();
            }
          }}
        />
      )}

      <Toaster 
        //position="top-right"
        expand={true}
        richColors
        duration={5000}
        visibleToasts={5}
        closeButton
      />
    </div>
  );
}

function AuthWrapper() {
  const { user, loading, subscription, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    const currentPath = location.pathname;
    if (currentPath === '/login') {
      return (
        <div className="min-h-screen bg-background">
          <Login
            onSwitchToSignup={() => navigate('/signup')}
            onNavigate={(route) => navigate(`/${route}`)}
          />
        </div>
      );
    } else if (currentPath === '/signup') {
      return (
        <div className="min-h-screen bg-background">
          <SignUp
            onSwitchToLogin={() => navigate('/login')}
            onNavigate={(route) => navigate(`/${route}`)}
          />
        </div>
      );
    } else {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  if (location.pathname === '/login' || location.pathname === '/signup') {
    return <Navigate to="/dashboard" replace />;
  }

  // Every profile is guaranteed a PERSONAL workgroup from the moment it's
  // created, so this is no longer "you have no organisation" — it's "your
  // active workgroup has no plan yet". AccountSetup collects that initial
  // company/usage info and picks a package, which is what actually creates
  // the subscription (see ProfileService.createOrganisation).
  if (user && !subscription) {
    return (
      <div className="min-h-screen bg-background">
        <AccountSetup
          onComplete={() => {
            refreshUser();
            navigate('/dashboard');
          }}
        />
      </div>
    );
  }

  return <AppLayout />;
}

function PublicRouteWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  if (path === '/terms-of-service') {
    return <TermsOfService onNavigate={(route) => navigate(`/${route}`)} />;
  } else if (path === '/privacy-policy') {
    return <PrivacyPolicy onNavigate={(route) => navigate(`/${route}`)} />;
  } else if (path === '/cookie-policy') {
    return <CookiePolicy />;
  } else if (path === '/gdpr-policy') {
    return <GDPRPolicy onNavigate={(route) => navigate(`/${route}`)} />;
  }
  //  else if (path==="/") {
  //   return <Landing/>;
  // }
  return null;
}

export default function App() {
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  useEffect(() => {
    const path = window.location.pathname;
    const resetPasswordMatch = path.match(/\/auth\/reset-password\/(.+)/);

    if (resetPasswordMatch) {
      const token = resetPasswordMatch[1];
      setResetToken(token);
      setShowResetPassword(true);
    }
  }, []);

  const handleCloseResetPassword = () => {
    setShowResetPassword(false);
    setResetToken(null);
    window.history.replaceState({}, '', '/');
  };

  const handleResetPasswordSuccess = () => {
    setShowResetPassword(false);
    setResetToken(null);
    window.history.replaceState({}, '', '/login');
  };

  if (showResetPassword && resetToken) {
    return (
      <ResetPassword
        isOpen={showResetPassword}
        onClose={handleCloseResetPassword}
        token={resetToken}
        onSuccess={handleResetPasswordSuccess}
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<PublicPricing />} />
      <Route path="/terms-of-service" element={<PublicRouteWrapper />} />
      <Route path="/privacy-policy" element={<PublicRouteWrapper />} />
      <Route path="/cookie-policy" element={<PublicRouteWrapper />} />
      <Route path="/gdpr-policy" element={<PublicRouteWrapper />} />

      <Route path="/verify-email/:token" element={<VerifyEmail />} />

      <Route path="/auth/github/callback" element={<GitHubCallback />} />

      <Route path="/login" element={<AuthWrapper />} />
      <Route path="/signup" element={<AuthWrapper />} />

      <Route path="/*" element={<AuthWrapper />} />
    </Routes>
  );
}
